/**
 * Stripe / Customer Portal post-redirect coordination.
 *
 * After a Stripe Checkout / Customer Portal flow Stripe redirects the
 * browser back to our return URL **in parallel** with webhook delivery —
 * neither side guarantees the order. The browser usually wins, so a fresh
 * page mount can render before the webhook has updated the wallet /
 * subscription. Without explicit handling the user sees stale state until
 * a manual refresh.
 *
 * We coordinate the two signals via a single query-string marker that
 * everywhere reads from one place:
 *
 * - **Producers** (every code path that builds a `return_url`) tag the URL
 *   with `?from_billing_redirect=1`. Today: tenant billing page checkouts
 *   (`components/billing/tenant-billing-page.tsx`) + new-tenant register
 *   flow (`app/api/auth/register/route.ts`).
 * - **Consumers** read the marker on mount: the tenant billing page polls
 *   `/api/billing/overview` for ~10 s and shows a «syncing…» banner;
 *   `LowBalanceBanner` suppresses itself for the same window so the user
 *   does not see a flash of «balance depleted» while the wallet is still
 *   being credited.
 *
 * The tenant billing page strips the marker via `router.replace` after
 * first read so a manual refresh during the polling window does not
 * re-trigger the loop. `LowBalanceBanner` instead snapshots the marker
 * once via a lazy `useState` initializer — its suppression decision is
 * latched at mount time and survives the concurrent strip from the
 * billing page.
 */

export const BILLING_REDIRECT_QUERY_PARAM = 'from_billing_redirect'

/**
 * Polling window length on the tenant billing page (1 Hz × 10 ticks). The
 * banner's suppression delay is set one second longer so it never reveals
 * itself before polling has had its last chance to settle the state.
 */
export const BILLING_REDIRECT_POLL_WINDOW_MS = 10_000
export const BILLING_REDIRECT_POLL_INTERVAL_MS = 1_000
export const BILLING_REDIRECT_BANNER_SUPPRESS_MS = 11_000
